import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, MagnifyingGlass, Trophy, CaretLeft, CaretRight, X } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { hour12: false }) : "—");

const SORTS = [
  ["neet_air", "NEET AIR"],
  ["state_rank", "State rank"],
  ["neet_score", "Score"],
  ["appl_no", "Appl no"],
];

/**
 * Read-only viewer for the `merit_list` collection (appl_no -> true NEET AIR).
 * Body + year come from server-provided facets; appl_no is an exact-match
 * lookup (the join key used to enrich raw_allotments with the true AIR).
 */
export default function AdminMeritList() {
  const [sp] = useSearchParams();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  // Pre-filter from the Ingestion Diary deep-link (?body=&year=).
  const [body, setBody]     = useState(sp.get("body") || "");
  const [year, setYear]     = useState(sp.get("year") || "");
  const [appl, setAppl]     = useState("");      // committed appl_no filter
  const [applInput, setApplInput] = useState(""); // text box value
  const [sort, setSort]     = useState("neet_air");
  const [page, setPage]     = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (body) params.set("counselling_body", body);
      if (year) params.set("year", year);
      if (appl) params.set("appl_no", appl);
      params.set("sort", sort);
      params.set("page", String(page));
      params.set("limit", "50");
      const r = await api.get(`/admin/merit-list?${params}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load merit list");
    } finally { setLoading(false); }
  };

  // Reset to page 1 whenever a filter changes.
  useEffect(() => { setPage(1); /* eslint-disable-next-line */ }, [body, year, appl, sort]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [body, year, appl, sort, page]);

  const facets  = data?.facets || { counselling_bodies: [], years: [] };
  const summary = data?.summary || {};
  const items   = data?.items || [];

  const doLookup = () => setAppl(applInput.trim());
  const clearLookup = () => { setApplInput(""); setAppl(""); };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-merit-list-page">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin" className="text-xs font-bold uppercase text-zinc-500 hover:text-[#6043EF] flex items-center gap-1"
            data-testid="back-to-admin">
            <ArrowLeft size={12}/> Admin
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B]">Merit List</h1>
          <span className="text-xs font-mono font-bold text-zinc-500" data-testid="merit-total">
            {summary.total != null ? `${summary.total.toLocaleString("en-IN")} rows` : ""}
          </span>
        </div>

        <p className="text-[12px] text-zinc-500 mb-5 max-w-2xl leading-relaxed">
          Candidate rank list keyed on <b>application number → true NEET All-India Rank</b>.
          This is the source that enriches <code className="font-mono text-[11px] bg-zinc-100 px-1 rounded">raw_allotments.all_india_rank</code> via
          the appl-no join. Read-only.
        </p>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="merit-filters">
          <Field label="Counselling body">
            <select
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="merit-filter-body"
              className="h-8 px-2 text-sm border-2 border-zinc-200 rounded-md bg-white min-w-[160px] focus:border-[#6043EF] outline-none"
            >
              <option value="">All bodies</option>
              {facets.counselling_bodies.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>

          <Field label="Year">
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              data-testid="merit-filter-year"
              className="h-8 px-2 text-sm border-2 border-zinc-200 rounded-md bg-white min-w-[100px] focus:border-[#6043EF] outline-none"
            >
              <option value="">All years</option>
              {facets.years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </Field>

          <Field label="Sort by">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="merit-filter-sort"
              className="h-8 px-2 text-sm border-2 border-zinc-200 rounded-md bg-white min-w-[120px] focus:border-[#6043EF] outline-none"
            >
              {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>

          <Field label="Lookup appl no">
            <div className="flex items-center gap-1">
              <input
                value={applInput}
                onChange={(e) => setApplInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doLookup()}
                placeholder="e.g. 1251699"
                data-testid="merit-filter-appl"
                className="h-8 px-2 text-sm border-2 border-zinc-200 rounded-md bg-white w-[150px] font-mono focus:border-[#6043EF] outline-none"
              />
              <button
                onClick={doLookup}
                data-testid="merit-lookup-btn"
                className="h-8 w-8 flex items-center justify-center rounded-md bg-[#6043EF] text-white hover:bg-[#4f36c9]"
                title="Find application number"
              ><MagnifyingGlass size={15} weight="bold" /></button>
              {appl && (
                <button
                  onClick={clearLookup}
                  data-testid="merit-lookup-clear"
                  className="h-8 w-8 flex items-center justify-center rounded-md bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                  title="Clear lookup"
                ><X size={15} weight="bold" /></button>
              )}
            </div>
          </Field>

          {loading && <span className="text-[11px] text-zinc-400 pb-1">Loading…</span>}
        </div>

        {/* Table */}
        <div className="border-2 border-zinc-200 rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="merit-table">
            <thead className="bg-zinc-50 border-b-2 border-zinc-200 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left">Appl no</th>
                <th className="px-3 py-2 text-left">Body</th>
                <th className="px-3 py-2 text-right">Year</th>
                <th className="px-3 py-2 text-right">NEET AIR</th>
                <th className="px-3 py-2 text-right">State rank</th>
                <th className="px-3 py-2 text-right">NEET score</th>
                <th className="px-3 py-2 text-left">Updated</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, i) => (
                <tr key={`${r.appl_no}-${i}`} data-testid={`merit-row-${r.appl_no}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-3 py-2 font-mono text-[12px] font-semibold">{r.appl_no}</td>
                  <td className="px-3 py-2 text-[12px] text-zinc-600">{r.counselling_body}</td>
                  <td className="px-3 py-2 text-right font-mono text-[12px] text-zinc-500">{r.year}</td>
                  <td className="px-3 py-2 text-right font-mono text-[12px] font-bold text-[#6043EF]">
                    {r.neet_air != null ? r.neet_air.toLocaleString("en-IN") : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-[12px]">{r.state_rank != null ? r.state_rank.toLocaleString("en-IN") : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-[12px]">{r.neet_score != null ? r.neet_score : "—"}</td>
                  <td className="px-3 py-2 text-[11px] font-mono text-zinc-400">{fmt(r.updated_at)}</td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-zinc-400 text-sm" data-testid="merit-empty">
                  <Trophy size={28} className="mx-auto mb-2 text-zinc-300" />
                  No merit rows match this filter.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {summary.pages > 1 && (
          <div className="flex items-center justify-between mt-3" data-testid="merit-pagination">
            <span className="text-[11px] text-zinc-500">
              Page <b>{summary.page}</b> of {summary.pages} · {summary.total.toLocaleString("en-IN")} rows
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                data-testid="merit-prev"
                className="h-8 px-3 flex items-center gap-1 text-xs font-bold rounded-md bg-zinc-100 text-zinc-700 hover:bg-zinc-200 disabled:opacity-40"
              ><CaretLeft size={13} weight="bold" /> Prev</button>
              <button
                onClick={() => setPage((p) => Math.min(summary.pages, p + 1))}
                disabled={page >= summary.pages}
                data-testid="merit-next"
                className="h-8 px-3 flex items-center gap-1 text-xs font-bold rounded-md bg-zinc-100 text-zinc-700 hover:bg-zinc-200 disabled:opacity-40"
              >Next <CaretRight size={13} weight="bold" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-zinc-500">{label}</label>
      {children}
    </div>
  );
}
